import React, { useState } from 'react';
import { Popover, Tooltip } from 'antd';

export function toColorInputValue(value: unknown): string {
  if (typeof value === 'string' && /^#[0-9a-fA-F]{6}$/i.test(value.trim())) {
    return value.trim();
  }
  return '#000000';
}

export function hasRibbonColorValue(value: unknown): boolean {
  return typeof value === 'string' && /^#[0-9a-fA-F]{3,8}$/i.test(value.trim());
}

type ExtraAction = { label: string; onClick: () => void };

type Props = {
  value?: string;
  onChange?: (value: string) => void;
  title: string;
  icon: React.ReactNode;
  /** When true, opens a popover with a remove-color action. */
  allowClear?: boolean;
  clearLabel?: string;
  extraActions?: ExtraAction[];
};

function ColorSwatchVisual({
  icon,
  hasColor,
  display,
}: {
  icon: React.ReactNode;
  hasColor: boolean;
  display: string;
}) {
  return (
    <span className="word-ribbon-color-swatch" aria-hidden>
      {icon}
      <span
        className="word-ribbon-color-bar"
        style={hasColor ? { background: display } : undefined}
      />
    </span>
  );
}

export const RibbonColorSwatch: React.FC<Props> = ({
  value,
  onChange,
  title,
  icon,
  allowClear = false,
  clearLabel = 'Remove color',
  extraActions,
}) => {
  const hasColor = hasRibbonColorValue(value);
  const display = hasColor ? toColorInputValue(value) : '#000000';
  const [open, setOpen] = useState(false);

  if (!allowClear) {
    return (
      <Tooltip title={title} placement="top" mouseEnterDelay={0.3}>
        <label
          className={`word-ribbon-color-label${hasColor ? ' word-ribbon-color-label--active' : ''}`}
        >
          <input
            type="color"
            className="word-ribbon-color-input-hidden"
            aria-label={title}
            value={display}
            onChange={(e) => onChange?.(e.target.value)}
          />
          <ColorSwatchVisual icon={icon} hasColor={hasColor} display={display} />
        </label>
      </Tooltip>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      overlayClassName="word-ribbon-color-popover-overlay"
      content={
        <div className="word-ribbon-color-popover">
          <label className="word-ribbon-color-popover-picker">
            <input
              type="color"
              className="word-ribbon-color-popover-input"
              aria-label={title}
              value={display}
              onChange={(e) => onChange?.(e.target.value)}
            />
            <span className="word-ribbon-color-popover-hex">{hasColor ? display : 'Pick color'}</span>
          </label>
          {hasColor ? (
            <button
              type="button"
              className="word-ribbon-color-popover-clear"
              onClick={() => {
                onChange?.('');
                setOpen(false);
              }}
            >
              {clearLabel}
            </button>
          ) : null}
          {extraActions?.map((action) => (
            <button
              key={action.label}
              type="button"
              className="word-ribbon-color-popover-clear"
              onClick={() => {
                action.onClick();
                setOpen(false);
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      }
    >
      <Tooltip title={title} placement="top" mouseEnterDelay={0.3}>
        <button
          type="button"
          aria-label={title}
          className={`word-ribbon-color-label word-ribbon-color-trigger${
            hasColor ? ' word-ribbon-color-label--active' : ''
          }`}
        >
          <ColorSwatchVisual icon={icon} hasColor={hasColor} display={display} />
        </button>
      </Tooltip>
    </Popover>
  );
};
